import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useAuth, useCanWrite } from "../../auth/AuthProvider";
import { ConfirmActionModal } from "../../components/ConfirmActionModal";
import { Icon } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  LoadingBlock,
  Mono,
  Select,
  Textarea,
  buttonStyles,
  cx,
} from "../../components/ui";
import { formatDateTime, formatRelative } from "../../lib/format";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  STATUS_TONE,
  waitingFor,
} from "../../lib/support";
import {
  useDeleteTicket,
  useReplyToTicket,
  useSupportTicket,
  useUpdateTicket,
  useUploadTicketAttachment,
} from "../../lib/queries";
import type {
  SupportCategory,
  SupportMessage,
  SupportPriority,
  SupportStatus,
  SupportTicketDetail,
} from "../../lib/types";
import { AttachmentPicker, TicketAttachment } from "./TicketAttachments";

/**
 * One ticket, from the operator's side: the whole thread including notes, the
 * composer, and the controls that move it.
 *
 * Everything here except deletion is available to `support`, not just
 * `superadmin` — which is an exception to this console's usual rule and a
 * deliberate one. Answering customers is what the support role exists for, and
 * a console where support staff can read the queue and not reply to it is a
 * console that has not shipped the feature.
 */
export function TicketDetailPage() {
  const { ticketId } = useParams();
  const { data: ticket, isPending, error } = useSupportTicket(ticketId);

  if (isPending) return <LoadingBlock label="Loading ticket…" />;
  if (error || !ticket) {
    return (
      <div className="space-y-5">
        <Alert>That ticket could not be loaded. It may have been deleted.</Alert>
        <Link to="/support" className={buttonStyles("secondary")}>
          Back to the queue
        </Link>
      </div>
    );
  }

  const tone = STATUS_TONE[ticket.status];
  const waiting = waitingFor(ticket);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-fg-subtle">
        <Link to="/support" className="transition-colors hover:text-fg">
          Support
        </Link>
        <Icon name="chevron" className="size-3" />
        <Mono className="text-xs">{ticket.reference}</Mono>
      </div>

      <header className="space-y-3 border-b border-line pb-5">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cx(
              "inline-flex items-center gap-1.5 border px-1.5 py-0.5 text-[11px] font-medium",
              tone.styles,
            )}
          >
            <span aria-hidden="true" className={cx("size-1.5 rounded-full", tone.dot)} />
            {STATUS_LABELS[ticket.status]}
          </span>
          <span className="text-sm text-fg-subtle">
            {CATEGORY_LABELS[ticket.category]} · {PRIORITY_LABELS[ticket.priority]} impact
          </span>
          {waiting && (
            <span className="text-sm text-warn">waiting {waiting}</span>
          )}
        </div>

        <h1 className="text-[1.375rem] font-normal tracking-[-0.022em] break-words text-fg">
          {ticket.subject}
        </h1>

        <p className="text-sm text-fg-muted">
          Opened by {ticket.openedByName ?? "a former member"}
          {ticket.openedByEmail && <> ({ticket.openedByEmail})</>} for{" "}
          <Link
            to={`/organizations/${ticket.orgId}`}
            className="text-accent transition-colors hover:text-accent-hover"
          >
            {ticket.orgName ?? "an organization"}
          </Link>
          {ticket.orgSuspendedAt && <span className="text-danger"> — currently suspended</span>}
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <ol className="space-y-4">
            {ticket.messages.map((message) => (
              <li key={message.id}>
                <Message message={message} ticketId={ticket.id} />
              </li>
            ))}
          </ol>

          <Composer ticket={ticket} />
        </div>

        <TicketControls ticket={ticket} />
      </div>
    </div>
  );
}

/**
 * One message.
 *
 * Three appearances rather than two: the customer's, ours, and an internal note
 * — which is dashed and amber precisely because it is the one thing on this
 * page the customer cannot see, and mistaking a note for a sent reply is the
 * mistake this layout exists to prevent.
 */
function Message({ message, ticketId }: { message: SupportMessage; ticketId: string }) {
  const fromStaff = message.authorRole === "staff";

  return (
    <article
      /*
       * Three appearances, and only two of them use colour.
       *
       * This console's accent *is* amber, so painting a note warn put it within
       * a hue of our own replies — the exact pair that must never be confused.
       * A note is sunk instead: darker than the panel rather than raised on it,
       * dashed, and labelled. Structure separates them where colour cannot.
       */
      className={cx(
        "border",
        message.internal
          ? "border-dashed border-line-strong bg-canvas"
          : fromStaff
            ? "border-accent/25 border-l-2 border-l-accent bg-raised"
            : "border-line bg-raised",
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm">
          <span
            className={cx(
              "font-medium",
              message.internal ? "text-fg-muted" : fromStaff ? "text-accent-hover" : "text-fg",
            )}
          >
            {message.authorName ?? "Former account"}
          </span>
          {message.internal ? (
            /* Spelled out rather than abbreviated. "Internal" is a word people
               skim; the sentence is what stops somebody pasting an account note
               into a reply. */
            <span className="border border-warn/30 bg-warn/8 px-1.5 py-0.5 text-[11px] text-warn">
              not sent to the customer
            </span>
          ) : (
            <span className="text-xs text-fg-subtle">{fromStaff ? "replied" : "customer"}</span>
          )}
        </span>
        <time
          dateTime={message.createdAt}
          title={formatDateTime(message.createdAt)}
          className="text-xs text-fg-subtle"
        >
          {formatRelative(message.createdAt)}
        </time>
      </header>

      {/* Plain text by construction — the API refuses markup — so `pre-wrap`
          and nothing else. A pasted stack trace reflowed into a paragraph is
          the single most common thing on this page and the least readable. */}
      <p className="px-4 py-3.5 text-sm leading-6 whitespace-pre-wrap break-words text-fg-muted">
        {message.body}
      </p>

      {message.attachments.length > 0 && (
        <div className="space-y-2 border-t border-line px-4 py-3">
          {message.attachments.map((attachment) => (
            <TicketAttachment key={attachment.id} attachment={attachment} ticketId={ticketId} />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * Writing back.
 *
 * Two modes on one control rather than two boxes, because they are the same
 * action with a different audience and an operator should never be in doubt
 * about which one they are in: the whole composer changes colour with the
 * choice, and the button says who will read it.
 */
function Composer({ ticket }: { ticket: SupportTicketDetail }) {
  const reply = useReplyToTicket(ticket.id);
  const upload = useUploadTicketAttachment(ticket.id);
  const [internal, setInternal] = useState(false);
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<SupportStatus | "">("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadFailed, setUploadFailed] = useState<string | null>(null);

  const busy = reply.isPending || upload.isPending;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || body.trim().length === 0) return;

    const message = await reply.mutateAsync({
      body: body.trim(),
      internal,
      ...(status ? { status } : {}),
    });

    let failed: string | null = null;
    for (const file of files) {
      try {
        await upload.mutateAsync({ messageId: message.id, file });
      } catch {
        failed = file.name;
      }
    }

    setUploadFailed(failed);
    setBody("");
    setFiles([]);
    setStatus("");
  };

  return (
    <Card className={internal ? "border-warn/40" : undefined}>
      <div className="flex border-b border-line">
        {[
          { value: false, label: "Reply to customer" },
          { value: true, label: "Internal note" },
        ].map((mode) => {
          const active = mode.value === internal;
          return (
            <button
              key={String(mode.value)}
              type="button"
              onClick={() => setInternal(mode.value)}
              aria-pressed={active}
              className={cx(
                "border-b-2 px-5 py-3 text-sm transition-colors duration-200 ease-smooth",
                active
                  ? mode.value
                    ? "border-warn text-warn"
                    : "border-accent text-fg"
                  : "border-transparent text-fg-subtle hover:text-fg-muted",
              )}
            >
              {mode.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={submit} className="space-y-4 p-5">
        {reply.error && <Alert>{reply.error.message}</Alert>}
        {uploadFailed && (
          <Alert tone="warning">
            The message was sent, but <strong>{uploadFailed}</strong> did not upload.
          </Alert>
        )}

        {internal && (
          <p className="text-xs text-warn">
            Only operators see this. The customer is not emailed, and the ticket does not move.
          </p>
        )}

        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={6}
          maxLength={10_000}
          aria-label={internal ? "Internal note" : "Reply to the customer"}
          placeholder={
            internal
              ? "What you checked, what the ledger said, who should pick this up…"
              : "Answer in plain language. This is emailed to whoever opened the ticket."
          }
        />

        <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" loading={busy} disabled={body.trim().length === 0}>
            <Icon name="send" className="size-3.5" />
            {internal ? "Save note" : "Send reply"}
          </Button>

          {/* Status rides along with the reply rather than being a second
              action. "Answered and resolved" is one thought, and making it two
              clicks is how tickets stay open after they are done. */}
          {!internal && (
            <label className="flex items-center gap-2 text-sm text-fg-subtle">
              then
              <div className="w-44">
                <Select
                  value={status}
                  onChange={(event) => setStatus(event.target.value as SupportStatus | "")}
                  aria-label="Status after sending"
                >
                  <option value="">await the customer</option>
                  <option value="resolved">mark resolved</option>
                  <option value="open">leave open</option>
                  <option value="closed">close it</option>
                </Select>
              </div>
            </label>
          )}
        </div>
      </form>
    </Card>
  );
}

/** Everything that moves the ticket without writing on it. */
function TicketControls({ ticket }: { ticket: SupportTicketDetail }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = useCanWrite();
  const update = useUpdateTicket(ticket.id);
  const remove = useDeleteTicket();
  const [confirming, setConfirming] = useState(false);

  const mine = ticket.assignedTo === user?.id;

  return (
    <aside className="min-w-0 space-y-4">
      <Card>
        <div className="space-y-4 p-5">
          <h2 className="text-sm font-medium text-fg">Handling</h2>

          {update.error && <Alert>{update.error.message}</Alert>}

          <label className="block space-y-1.5">
            <span className="text-xs text-fg-subtle">Status</span>
            <Select
              value={ticket.status}
              disabled={update.isPending}
              onChange={(event) =>
                update.mutate({ status: event.target.value as SupportStatus })
              }
            >
              {(["open", "pending", "resolved", "closed"] as SupportStatus[]).map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs text-fg-subtle">Impact</span>
            <Select
              value={ticket.priority}
              disabled={update.isPending}
              onChange={(event) =>
                update.mutate({ priority: event.target.value as SupportPriority })
              }
            >
              {(["low", "normal", "high", "urgent"] as SupportPriority[]).map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs text-fg-subtle">Kind</span>
            <Select
              value={ticket.category}
              disabled={update.isPending}
              onChange={(event) =>
                update.mutate({ category: event.target.value as SupportCategory })
              }
            >
              {(
                ["technical", "billing", "account", "abuse", "other"] as SupportCategory[]
              ).map((value) => (
                <option key={value} value={value}>
                  {CATEGORY_LABELS[value]}
                </option>
              ))}
            </Select>
          </label>

          {/*
            Assignment is a self-service claim rather than a picker over a staff
            directory. It is advisory — anybody can still answer — and what an
            operator actually does is take a ticket or put it back, which two
            buttons say better than a dropdown of colleagues.
          */}
          <div className="space-y-1.5 border-t border-line pt-4">
            <span className="text-xs text-fg-subtle">Assignee</span>
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate text-sm text-fg-muted">
                {ticket.assignedToName ?? "Nobody yet"}
              </span>
              <Button
                type="button"
                variant="secondary"
                disabled={update.isPending}
                onClick={() => update.mutate({ assignedTo: mine ? null : (user?.id ?? null) })}
              >
                {mine ? "Release" : "Take it"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="space-y-2.5 p-5 text-sm">
          <h2 className="text-sm font-medium text-fg">Facts</h2>
          <Fact label="Reference">
            <Mono className="text-xs">{ticket.reference}</Mono>
          </Fact>
          <Fact label="Opened">
            <span title={formatDateTime(ticket.createdAt)}>
              {formatRelative(ticket.createdAt)}
            </span>
          </Fact>
          <Fact label="First reply">
            {ticket.firstReplyAt ? (
              <span title={formatDateTime(ticket.firstReplyAt)}>
                {formatRelative(ticket.firstReplyAt)}
              </span>
            ) : (
              <span className="text-warn">not yet</span>
            )}
          </Fact>
          <Fact label="Messages">{ticket.messages.length}</Fact>
          {ticket.resolvedAt && (
            <Fact label="Resolved">
              <span title={formatDateTime(ticket.resolvedAt)}>
                {formatRelative(ticket.resolvedAt)}
              </span>
            </Fact>
          )}

          <div className="space-y-1.5 pt-2">
            <Link
              to={`/organizations/${ticket.orgId}`}
              className="flex items-center gap-2 border border-line px-2.5 py-2 text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg"
            >
              <Icon name="orgs" className="size-3.5 shrink-0 text-fg-subtle" />
              <span className="min-w-0 flex-1 truncate">{ticket.orgName ?? "Organization"}</span>
            </Link>

            {ticket.appId && (
              <Link
                to={`/apps/${ticket.appId}`}
                className="flex items-center gap-2 border border-line px-2.5 py-2 text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg"
              >
                <Icon name="apps" className="size-3.5 shrink-0 text-fg-subtle" />
                <span className="min-w-0 flex-1 truncate">{ticket.appName ?? "Application"}</span>
              </Link>
            )}

            {ticket.databaseId && (
              <span className="flex items-center gap-2 border border-line px-2.5 py-2 text-sm text-fg-muted">
                <Icon name="databases" className="size-3.5 shrink-0 text-fg-subtle" />
                <span className="min-w-0 flex-1 truncate">
                  {ticket.databaseName ?? "Database"}
                </span>
              </span>
            )}
          </div>
        </div>
      </Card>

      {/*
        The one destructive action, and the only thing here a support operator
        cannot do. Resolving is how a ticket ends; this is for spam and for the
        customer who pasted a live credential into a message and wants it gone.
      */}
      {canDelete && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="flex w-full items-center justify-center gap-2 border border-line px-3 py-2 text-sm text-fg-subtle transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger"
        >
          <Icon name="trash" className="size-3.5" />
          Delete ticket and files
        </button>
      )}

      <ConfirmActionModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() =>
          remove.mutate(ticket.id, {
            onSuccess: () => {
              setConfirming(false);
              void navigate("/support");
            },
          })
        }
        title={`Delete ${ticket.reference}?`}
        description={ticket.subject}
        consequence={
          <>
            The thread and every file on it are destroyed, for the customer as well as for us. The
            audit log keeps a record that this happened. Resolving is the normal way to end a
            ticket — use this for spam, or to destroy a credential somebody pasted.
          </>
        }
        confirmLabel="Delete permanently"
        danger
        loading={remove.isPending}
        error={remove.error}
      />
    </aside>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-fg-subtle">{label}</span>
      <span className="min-w-0 truncate text-right text-fg-muted">{children}</span>
    </div>
  );
}
